import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ordersService } from "../../../services";

import css from "./DeliveriesSComponent.module.css";
import { Table } from "antd";

const DeliveriesSComponent = () => {
  const { t } = useTranslation();
  const [statisticsData, setStatisticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateColumns, setDateColumns] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientModal, setShowClientModal] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [, month, day] = dateString.split("-");
    return `${day}.${month}`; // For column headers - no year
  };

  const formatDateWithYear = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}.${month}.${year.slice(-2)}`; // For Last delivery column - with year
  };

  const fetchDeliveryStatistics = async () => {
    try {
      setLoading(true);
      const data = await ordersService.getDeliveryStatistics();

      if (data && data.length > 0) {
        // Get all unique dates from all orders and sort them
        const allDates = new Set();
        data.forEach((client) => {
          client.deliveryDates.forEach((date) => allDates.add(date));
        });

        // Filter dates to only show from today onwards
        // Use local date to avoid timezone issues
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        const todayString = `${year}-${month}-${day}`;

        const filteredDates = Array.from(allDates).filter(
          (date) => date >= todayString
        );
        const sortedDates = filteredDates.sort(
          (a, b) => new Date(a) - new Date(b)
        );

        setDateColumns(sortedDates);
        setStatisticsData(data);
      }
    } catch (error) {
      console.error("Error fetching delivery statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveryStatistics();
  }, []);

  const hasDeliveryOnDate = (client, date) => {
    return client.deliveryDates.includes(date);
  };

  const getDeliveryCount = (client, date) => {
    return client.deliveryDatesCounts ? client.deliveryDatesCounts[date] : 0;
  };

  const handleRowClick = async (client) => {
    try {
      const details = client.clientDetails;
      const addressParts = [
        details?.devlieryCity,
        details?.deliveryStreet,
        details?.buidlingNumber,
        details?.entranceNumber ? `Entrance ${details.entranceNumber}` : "",
        details?.floorNumber ? `Floor ${details.floorNumber}` : "",
        details?.apartmnetNumber ? `Apt ${details.apartmnetNumber}` : "",
      ].filter(Boolean);

      const fullAddress =
        addressParts.length > 0
          ? addressParts.join(", ")
          : "Address not available";

      setSelectedClient({
        phone: details?.phoneNumber || client.clientContact,
        name: client.clientName,
        address: fullAddress,
        contact: `${details?.communicationMethod || "Phone"}: ${
          client.clientContact
        }${
          details?.communicationDetails
            ? ` (${details.communicationDetails})`
            : ""
        }`,
        points: details?.points || 0,
        deliveryInterval: details?.deleveryInterval || "Not specified",
        deliveryComment: details?.commentForDelivery || "No comments",
        ordersLastDeliveryDates: client.ordersLastDeliveryDates,
      });
      setShowClientModal(true);
    } catch (error) {
      console.error("Error preparing client details:", error);
    }
  };

  const closeModal = () => {
    setShowClientModal(false);
    setSelectedClient(null);
  };

  const lastDeliveryDateColumns = [
    {
      title: `${t("Order Id")} #`,
      dataIndex: "orderId",
      key: "orderId",
      width: 100,
    },
    {
      title: `${t("Last Delivery Date")}`,
      dataIndex: "date",
      key: "date",
      width: 150,
    },
  ];

  if (loading) {
    return (
      <section className={css.wrap}>
        <div className={css.loading}>Loading delivery statistics...</div>
      </section>
    );
  }

  return (
    <section className={css.wrap}>
      <div className={css.tableContainer}>
        <table className={css.table}>
          <thead>
            <tr className={css.headerRow}>
              <th className={css.headerCell}>#</th>
              <th className={css.headerCell}>{t("client")}</th>
              <th className={css.headerCell}>{t("last-delivery")}</th>
              {dateColumns.map((date) => (
                <th key={date} className={css.headerCell}>
                  {formatDate(date)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statisticsData.map((client, index) => (
              <tr
                key={client.id}
                className={css.dataRow}
                onClick={() => handleRowClick(client)}
              >
                <td className={css.dataCell}>{index + 1}</td>
                <td className={css.dataCell}>{client.clientName}</td>
                <td className={css.dataCell}>
                  {client.lastDeliveryDate
                    ? formatDateWithYear(client.lastDeliveryDate)
                    : "-"}
                </td>
                {dateColumns.map((date) => {
                  const hasDelivery = hasDeliveryOnDate(client, date);
                  const deliveryCount = getDeliveryCount(client, date);
                  //   const isLastDelivery = client.lastDeliveryDate === date;
                  const ordersLastDeliveryDates = Object.values(
                    client.ordersLastDeliveryDates
                  );
                  const isLastDelivery = ordersLastDeliveryDates.includes(date);
                  return (
                    <td
                      key={date}
                      className={`${css.dataCell} ${
                        hasDelivery ? css.deliveryCell : ""
                      }`}
                    >
                      {hasDelivery && (deliveryCount > 1 || isLastDelivery) && (
                        <div
                          className={`${css.deliveryIndicator} ${
                            isLastDelivery ? css.lastDeliveryIndicator : ""
                          }`}
                        >
                          {deliveryCount > 1 ? deliveryCount : ""}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {statisticsData.length === 0 && (
        <div className={css.noData}>{t("no-data")}</div>
      )}

      {/* Client Details Modal */}
      {showClientModal && selectedClient && (
        <div className={css.modalOverlay} onClick={closeModal}>
          <div
            className={css.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={css.modalHeader}>
              <h3>{t("client-details")}</h3>
              <button className={css.closeButton} onClick={closeModal}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.detailRow}>
                <span className={css.detailLabel}>{t("registered-on")}</span>
                <span className={css.detailValue}>{selectedClient.phone}</span>
              </div>
              <div className={css.detailRow}>
                <span className={css.detailLabel}>{t("name")}</span>
                <span className={css.detailValue}>{selectedClient.name}</span>
              </div>
              <div className={css.detailRow}>
                <span className={css.detailLabel}>{t("address")}</span>
                <span className={css.detailValue}>
                  {selectedClient.address}
                </span>
              </div>
              <div className={css.detailRow}>
                <span className={css.detailLabel}>{t("contact")}</span>
                <span className={css.detailValue}>
                  {selectedClient.contact}
                </span>
              </div>
              <div className={css.detailRow}>
                <span className={css.detailLabel}>{t("points")}</span>
                <span className={css.detailValue}>{selectedClient.points}</span>
              </div>
              <div className={css.detailRow}>
                <span className={css.detailLabel}>
                  {t("delivery-interval")}
                </span>
                <span className={css.detailValue}>
                  {selectedClient.deliveryInterval}
                </span>
              </div>
              <div className={css.detailRow}>
                <span className={css.detailLabel}>{t("delivery-comment")}</span>
                <span className={css.detailValue}>
                  {selectedClient.deliveryComment}
                </span>
              </div>

              {/* Ant Design Table for client's last delivery dates */}
              <div className={css.detailRow}>
                <div
                  className={css.detailValue}
                  style={{ width: "100%", marginTop: 8 }}
                >
                  <Table
                    size="small"
                    bordered
                    pagination={false}
                    rowKey="orderId"
                    columns={lastDeliveryDateColumns}
                    dataSource={
                      selectedClient.ordersLastDeliveryDates
                        ? Object.entries(selectedClient.ordersLastDeliveryDates)
                            ?.map(([orderId, date]) => {
                              const today = new Date();
                              if (new Date(date) > today) {
                                return {
                                  orderId,
                                  date,
                                };
                              }
                            })
                            .filter(Boolean)
                        : []
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export { DeliveriesSComponent };
